import React from 'react';
import PropTypes from 'prop-types';
import { MDText } from 'i18n-react';
import classNames from 'classnames';
import { renderToString } from 'react-dom/server';

import Button from '@adsk/bim360-matrix-react-button';
import Text from '@adsk/bim360-matrix-react-text';
import AnimateHeight from '@adsk/bim360-matrix-react-animate-height';

import linkifyText from '@adsk/bim360-matrix-react-helpers/es/linkifyText';
import {
  calculateVisibleText,
  removeVisibleText
} from '@adsk/bim360-matrix-react-helpers/es/visibleTextHelpers';
import { ELLIPSIS_CHAR } from '@adsk/bim360-matrix-react-helpers/es/consts';
import parseMentions from '@adsk/bim360-matrix-react-helpers/es/parseMentions';
import { withLocale, TSymbolProp } from '@adsk/bim360-matrix-react-helpers/es/locale';
import defaultSanitizeOptions from '@adsk/bim360-matrix-react-helpers/es/defaultSanitizeOptions';
import { ThemeConsumer } from '@adsk/bim360-matrix-react-helpers/es/theme';
import warn from '@adsk/bim360-matrix-react-helpers/es/warn';

import './_ActivityCommentBlock.scss';

export class ActivityCommentBlock extends React.Component {
  constructor(props) {
    super(props);

    this.toggleExpanded = this.toggleExpanded.bind(this);
    this.calcHeight = this.calcHeight.bind(this);

    if (!props.escapeHtml) {
      warn(`Warning: Running with HTML escape disabled, in the future, HTML escape for comments will be enabled by default.
If you are relying on there not being HTML escape for your comments, it is recommended that you pass escapeHtml={false} to <ActivityCommentBlock />
You can silence this warning by passing escapeHtml={true} to <ActivityCommentBlock />`);
    }

    const isExpanded = this.rebuildComponent(props);
    this.state = { isExpanded, height: '' };

    this.sanitizeOptions = defaultSanitizeOptions();
    this.sanitizeOptions.allowedTags.push('button');
    this.sanitizeOptions.allowedAttributes.button = ['class', 'type'];
  }

  rebuildComponent(props) {
    let isExpanded = true;
    this.parsedTextArray = parseMentions(
      props.comment,
      props.projectEntities,
      props.currentUser,
      props.mentionClasses,
      props.mentionRegex,
      props.locale,
      props.inactiveUserText,
      { escapeHtml: props.escapeHtml }
    );
    this.parsedText = linkifyText(this.parsedTextArray.join(''));
    const parsedTextLength = calculateVisibleText(this.parsedText);
    if (parsedTextLength > props.shortCommentMaxCharLength) {
      isExpanded = false;
      const buttonComponent = label => (
        <Button styleType={Button.StyleType.LINK} className="ActivityCommentBlock__button">
          {label}
        </Button>
      );

      this.parsedTruncatedText =
        removeVisibleText(this.parsedText, props.shortCommentMaxCharLength) + ELLIPSIS_CHAR;
      this.parsedTruncatedText +=
        ' ' + renderToString(buttonComponent(this.props[TSymbolProp].translate('SHOW')));
      this.parsedText +=
        ' ' + renderToString(buttonComponent(this.props[TSymbolProp].translate('HIDE')));
      this.attachOnclick = true;
    }

    return isExpanded;
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    const isExpanded = this.rebuildComponent(nextProps);
    this.setState({ isExpanded });
  }

  componentDidUpdate() {
    if (this.attachOnclick) {
      this.attachShowMoreEvent();
    }
  }

  componentDidMount() {
    if (this.attachOnclick) {
      this.attachShowMoreEvent();
    }
    this.calcHeight();
  }

  componentWillUnmount() {
    if (this.domElement) {
      this.domElement.removeEventListener('click', this.toggleExpanded);
    }
  }

  calcHeight() {
    if (!this.state.height || !this.state.isExpanded) {
      this.setState({
        height: `${this.blockNode.clientHeight}px`
      });
    }
  }

  attachShowMoreEvent() {
    this.domElement = this.blockNode.querySelector('.ActivityCommentBlock__button');
    if (this.domElement) {
      this.domElement.addEventListener('click', this.toggleExpanded);
      this.attachOnclick = false;
    }
  }

  toggleExpanded() {
    this.attachOnclick = true;
    this.setState({ isExpanded: !this.state.isExpanded });
  }

  render() {
    return (
      <ThemeConsumer>
        {({ theme }) => (
          <div
            className={classNames(
              'ActivityCommentBlock',
              this.props.className,
              `ActivityCommentBlock--${theme}`
            )}
            style={this.props.style}
            ref={_ => (this.blockNode = _)}
          >
            <span className="ActivityCommentBlock__field-value">
              <AnimateHeight
                isOpen={this.state.isExpanded}
                timeout={300}
                height={this.state.height}
                onExited={this.calcHeight}
              >
                <Text
                  containerClassName="ActivityCommentBlock__comment"
                  hasHTML={true}
                  parseNewLines={true}
                  sanitizeOptions={this.sanitizeOptions}
                >
                  {this.state.isExpanded ? this.parsedText : this.parsedTruncatedText}
                </Text>
              </AnimateHeight>
            </span>
          </div>
        )}
      </ThemeConsumer>
    );
  }
}

ActivityCommentBlock.defaultProps = {
  shortCommentMaxCharLength: 500,
  mentionClasses: {},
  mentionRegex: {},
  style: {},
  escapeHtml: false
};

ActivityCommentBlock.propTypes = {
  /**
   * Optional class name for the component
   */
  className: PropTypes.string,
  /**
   * Custom style for inline styling
   */
  style: PropTypes.object,
  /**
   * The comment's text. To view mentions - wrap the mention with the following HTML structure:
   * <span class="ActivityCommentBlock__mention">@Omri Aharon</span>
   * Use the helper function "parseMentions" in your application to parse the mention template into a viewable mention.
   */
  comment: PropTypes.string.isRequired,
  /**
   * A regex object to identify mention parts. See parseMentions.js for further info
   */
  mentionRegex: PropTypes.shape({
    /**
     * Regex to identify the mention template
     */
    mentionRegex: PropTypes.object,
    /**
     * Regex to identify the ID part of the mention
     */
    idRegex: PropTypes.object,
    /**
     * Regex to identify the display name part of the regex
     */
    displayRegex: PropTypes.object
  }),
  /**
   * A list of the project entities. Required because of the mentions logic
   */
  projectEntities: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      name: PropTypes.string
    })
  ).isRequired,
  /**
   * The current user object. Needed if you want to parse comments and highlight the comments directed at the current user. Required because of the mentions logic
   */
  currentUser: PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string
  }).isRequired,
  /**
   * Max chars for a comment without the show/hide button. Longer comments will be truncated and the show/hide button will be visible.
   * Default to 500
   */
  shortCommentMaxCharLength: PropTypes.number,
  /**
   * The locale to use for the show/hide button.
   */
  locale: PropTypes.string,
  /**
   * Object of mention/ownMention classes to provide to the mention
   */
  mentionClasses: PropTypes.shape({
    /**
     * Class to add to each mention
     */
    mention: PropTypes.string,
    /**
     * Class to add to own mentions
     */
    ownMention: PropTypes.string,
    /**
     * Class to add to inactive mentions
     */
    inactiveMention: PropTypes.string
  }),
  /**
   * Activates HTML escaping for comment text and username
   * NOTE: Will become default: true, Eventually™
   * @default false
   */
  escapeHtml: PropTypes.bool,
  /**
   * The text to display after the deleted user. Default value: former User
   */
  inactiveUserText: PropTypes.string
};

export default withLocale(
  ActivityCommentBlock,
  ({ locale }) => new MDText(require(`./i18n/${locale}.json`))
);
